import { saveAs } from 'file-saver'
import { FormProvider, useForm, useWatch } from 'react-hook-form'
import { useDispatch } from 'react-redux'

import { Card } from '@/components/Elements'
import { DatePicker } from '@/components/Form'
import useAnnouncement from '@/features/announcements/useAnnouncement'
import { hideLoadingOverlay, showLoadingOverlay } from '@/stores/ui/loadingOverlaySlice'
import { isNullish } from '@/utils/helper'
import { Box, Button, Flex, Text } from '@chakra-ui/react'
import { useEffect } from 'react'
import { toast } from 'react-toastify'
import { camelToWords } from '@/utils/stringHelper'

const AnnouncementCard = ({ type }) => {
  const methods = useForm()
  const dispatch = useDispatch()

  const { useFetchAnnouncementByType, useUpdateAnnouncement, useCreateAnnouncement } = useAnnouncement()
  const updateAnnouncement = useUpdateAnnouncement()
  const createAnnouncement = useCreateAnnouncement()
  const { data: announcementResponse } = useFetchAnnouncementByType(type)

  console.log(announcementResponse)

  const contentHtml = useWatch({ control: methods.control, name: 'contentHtml' })

  useEffect(() => {
    if (!isNullish(announcementResponse?.data)) {
      const announcement = announcementResponse.data
      methods.setValue('startDate', announcement.periodStart || announcement.startDate)
      methods.setValue('endDate', announcement.periodEnd || announcement.endDate)
      methods.setValue('contentHtml', announcement.contentHtml || '')
    }
  }, [announcementResponse, methods])

  const handleUploadHtmlFile = async (event) => {
    const file = event.target.files[0]
    if (!file) return

    const reader = new FileReader()
    reader.onload = (e) => {
      const content = e.target.result
      methods.setValue('contentHtml', content)
    }
    reader.readAsText(file)
  }

  const handleDownloadHtml = () => {
    const htmlContent = methods.getValues('contentHtml')
    const blob = new Blob([htmlContent], { type: 'text/html' })
    saveAs(blob, `announcement-${type}.html`)
  }

  const handleRemoveHtml = () => {
    methods.setValue('contentHtml', '')
  }

  const onSubmit = async (data) => {
    try {
      // Validation
      if (!data.startDate) {
        toast.error('Start Date is required')
        return
      }

      if (!data.endDate) {
        toast.error('End Date is required')
        return
      }

      if (!data.contentHtml || data.contentHtml.trim() === '') {
        toast.error('Announcement content is required')
        return
      }

      const startDate = new Date(data.startDate)
      const endDate = new Date(data.endDate)

      if (endDate < startDate) {
        toast.error('End Date must be after or equal to Start Date')
        return
      }

      dispatch(showLoadingOverlay())

      if (isNullish(announcementResponse?.data?._id)) {
        // Going to create announcement
        await createAnnouncement.mutateAsync({
          type,
          startDate: data.startDate,
          endDate: data.endDate,
          contentHtml: data.contentHtml,
        })
      } else if (announcementResponse?.data?._id) {
        // Update existing announcement
        await updateAnnouncement.mutateAsync({
          id: announcementResponse.data._id,
          data: {
            startDate: data.startDate,
            endDate: data.endDate,
            contentHtml: data.contentHtml,
          },
        })
      }
    } catch (err) {
      console.log(err)
      toast.error(err?.message || 'Failed to save announcement')
    } finally {
      dispatch(hideLoadingOverlay())
    }
  }
  return (
    <Card title={`${camelToWords(type)} Announcement`}>
      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(onSubmit)}>
          <Box p={5}>
            <Flex direction={'column'} gap="4">
              <DatePicker name="startDate" label="Start Date (DD-MMM-YYYY)" isRow />
              <DatePicker name="endDate" label="End Date (DD-MMM-YYYY)" isRow />
              <Flex alignItems="center">
                <Text w="30%" fontWeight={'semibold'}>
                  Announcement (.html)
                </Text>
                {contentHtml ? (
                  <Flex gap={2}>
                    <Button size="md" colorScheme="green" onClick={handleDownloadHtml}>
                      Download
                    </Button>
                    <Button size="md" colorScheme="red" onClick={handleRemoveHtml}>
                      Remove
                    </Button>
                  </Flex>
                ) : (
                  <input type="file" accept="text/html" onChange={handleUploadHtmlFile} />
                )}
              </Flex>

              {contentHtml && (
                <Box>
                  <Text fontWeight={'semibold'} mb={2}>
                    Preview:
                  </Text>
                  <Box
                    border="1px solid"
                    borderColor="gray.300"
                    borderRadius="md"
                    p={4}
                    minH="200px"
                    dangerouslySetInnerHTML={{ __html: contentHtml }}
                  />
                </Box>
              )}
            </Flex>
            <Button mt="6" colorScheme="blue" type="submit">
              Save
            </Button>
          </Box>
        </form>
      </FormProvider>
    </Card>
  )
}

export default AnnouncementCard
